import { App } from '@capacitor/app';
import { useEffect, useRef } from 'react';
import { isNativeApp } from '../native';

/**
 * Zurück-Taste und Neuladen an der Kasse: Der Warenkorb liegt nur im Arbeitsspeicher. Ein Druck auf die Android-Taste
 * oder ein Neuladen ließe ihn still verschwinden. Die Entscheidung ist reine Logik (`backAction`), das Einhängen
 * (`useBackButton`, `useUnloadGuard`) ist dünn.
 */

export interface BackState {
  menuOpen: boolean;
  /** Ein Blatt des Kassen-Modus (Verbindung, Verkäufe, Geräte-Check, Nummer, Artikel, Letzter Verkauf). */
  panelOpen: boolean;
  stage: 'scan' | 'pay' | 'done';
  warning: boolean;
  refConfirm: boolean;
  parkHint: boolean;
  /** Rückfrage „Kasse verlassen?“ ist offen. */
  leaveAsk: boolean;
  /** Rückfrage „Stornieren?“ ist offen. */
  voidAsk: boolean;
  cartCount: number;
}

export type BackAction =
  | 'close-menu'
  | 'cancel-void'
  | 'cancel-park-hint'
  | 'cancel-ref'
  | 'cancel-warning'
  | 'close-leave'
  | 'close-pay'
  | 'close-panel'
  | 'ask-leave'
  | 'leave';

/**
 * Was die Zurück-Taste tut: zuerst das oberste Blatt (Rückfragen vor Bezahlen vor übrigen Blättern), dann das Menü; nur
 * ohne offenes Blatt verlässt sie die Kasse, bei vollem Warenkorb erst nach Rückfrage.
 */
export function backAction(state: BackState): BackAction {
  if (state.voidAsk) return 'cancel-void';
  if (state.leaveAsk) return 'close-leave';
  if (state.parkHint) return 'cancel-park-hint';
  if (state.refConfirm) return 'cancel-ref';
  if (state.warning) return 'cancel-warning';
  if (state.stage === 'pay') return 'close-pay';
  if (state.panelOpen) return 'close-panel';
  if (state.menuOpen) return 'close-menu';
  return state.cartCount > 0 ? 'ask-leave' : 'leave';
}

/** Verlässt die Kasse: einen Schritt zurück, am Anfang der Verlaufs beendet die App sich (nur in der Hülle). */
export function leaveRegister(canGoBack: boolean): void {
  if (canGoBack) {
    window.history.back();
    return;
  }
  void App.exitApp().catch(() => undefined);
}

/**
 * Hängt die Android-Zurück-Taste der Hülle ein. Im Browser ohne Wirkung (dort schützt `useUnloadGuard`). Mit einem
 * Hörer übernimmt die Web-App die Taste ganz: Sie muss das Verlassen selbst auslösen (`leaveRegister`).
 */
export function useBackButton(onBack: (canGoBack: boolean) => void): void {
  const latest = useRef(onBack);
  useEffect(() => {
    latest.current = onBack;
  });
  useEffect(() => {
    if (!isNativeApp()) return;
    const listener = App.addListener('backButton', (event) => {
      latest.current(event.canGoBack);
    });
    return () => {
      void listener.then((registered) => registered.remove()).catch(() => undefined);
    };
  }, []);
}

/** Solange `active` gilt (Warenkorb nicht leer), fragt der Browser vor dem Neuladen oder Schließen des Tabs nach. */
export function useUnloadGuard(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    const onUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', onUnload);
    return () => {
      window.removeEventListener('beforeunload', onUnload);
    };
  }, [active]);
}
